import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, MessageOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { chatAPI, doctorLinkAPI, doctorsAPI, fileAPI } from '../utils/api';
import { getDownloadFileName, getFilePreviewUrl } from '../utils/fileUrls';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ACCESS_LABELS: Record<string, string> = {
  ultrasound_only: 'Только УЗИ',
  ultrasound_labs: 'УЗИ и анализы',
  full: 'Весь дневник',
};

const COLUMN_LABELS: Record<string, string> = {
  timestamp: 'Дата и время',
  glucose_level: 'Глюкоза, ммоль/л',
  meal_type: 'Прием пищи',
  measurement_time: 'Время измерения',
  entry_date: 'Дата',
  weight_kg: 'Вес, кг',
  movement_time: 'Время шевеления',
  symptom_text: 'Симптом',
  intensity: 'Интенсивность',
  abdomen_cm: 'Окружность живота, см',
  photo_url: 'Файл',
  doctor_name: 'Врач',
  notes: 'Комментарий',
  document_url: 'Документ',
  test_date: 'Дата анализа',
  test_type: 'Тип анализа',
  total_calories: 'Калории',
  total_protein: 'Белки',
  total_fat: 'Жиры',
  total_carbs: 'Углеводы',
  foods: 'Продукты',
  created_at: 'Создано',
};

const MEAL_TYPE_LABELS: Record<string, string> = {
  breakfast: 'Завтрак',
  lunch: 'Обед',
  dinner: 'Ужин',
  snack: 'Перекус',
};

const MEASUREMENT_TIME_LABELS: Record<string, string> = {
  before: 'До еды',
  '1h_after': 'Через 1 час',
  '2h_after': 'Через 2 часа',
};

const DIARY_SECTIONS = [
  { key: 'glucose', label: 'Глюкоза', loader: (id: number) => doctorsAPI.getPatientGlucoseEntries(id) },
  { key: 'meals', label: 'Питание', loader: (id: number) => doctorsAPI.getPatientMealEntries(id) },
  { key: 'weight', label: 'Вес', loader: (id: number) => doctorsAPI.getPatientWeightEntries(id) },
  { key: 'movements', label: 'Шевеления', loader: (id: number) => doctorsAPI.getPatientMovementEntries(id) },
  { key: 'symptoms', label: 'Симптомы', loader: (id: number) => doctorsAPI.getPatientSymptomEntries(id) },
  { key: 'abdomen', label: 'Окружность живота', loader: (id: number) => doctorsAPI.getPatientAbdomenEntries(id) },
  { key: 'visits', label: 'Посещения', loader: (id: number) => doctorsAPI.getPatientVisitEntries(id) },
  { key: 'labTests', label: 'Анализы', loader: (id: number) => doctorsAPI.getPatientLabTestEntries(id) },
] as const;

const humanizeKey = (key: string) =>
  COLUMN_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const isDiaryFileUrl = (key: string, value: unknown): boolean => {
  if (typeof value !== 'string' || !value) return false;
  if (key === 'photo_url' || key === 'document_url' || key === 'file_url' || key === 'avatar_url') {
    return true;
  }
  return (
    /^https?:\/\//.test(value) ||
    value.startsWith('/uploads/') ||
    value.includes(':9000/') ||
    value.includes('pregnancy-monitoring-')
  );
};

const openDiaryFile = (fileUrl: string) => {
  const previewUrl = getFilePreviewUrl(fileUrl);
  if (!previewUrl) {
    message.error('Файл недоступен');
    return;
  }
  window.open(previewUrl, '_blank', 'noopener,noreferrer');
};

const downloadDiaryFile = async (fileUrl: string, key: string) => {
  try {
    const response = await fileAPI.downloadFile(fileUrl);
    const blob = new Blob([response.data as BlobPart]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = getDownloadFileName(fileUrl, key === 'document_url' ? 'document' : 'file');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading diary file:', error);
    message.error('Не удалось скачать файл');
  }
};

const formatValue = (key: string, value: any) => {
  if (value === null || value === undefined || value === '') return '—';
  if (key === 'meal_type') return MEAL_TYPE_LABELS[String(value)] || String(value);
  if (key === 'measurement_time') return MEASUREMENT_TIME_LABELS[String(value)] || String(value);
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) && !isDiaryFileUrl(key, value)) {
    return dayjs(value).format(value.includes('T') ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY');
  }
  if (isDiaryFileUrl(key, value)) {
    return (
      <Space size="small">
        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openDiaryFile(value)}>
          Открыть
        </Button>
        <Button
          type="link"
          size="small"
          icon={<DownloadOutlined />}
          style={{ padding: 0 }}
          onClick={() => void downloadDiaryFile(value, key)}
        >
          Скачать
        </Button>
      </Space>
    );
  }
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item : item?.name || item?.label || JSON.stringify(item)))
      .join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export const PatientDetailPage: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [diaries, setDiaries] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveForm] = Form.useForm();
  const [diaryError, setDiaryError] = useState<string | null>(null);

  const loadPatientWorkspace = useCallback(async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      setDiaryError(null);
      const [detailRes, summaryRes] = await Promise.all([
        doctorsAPI.getPatientDetails(Number(patientId)),
        doctorsAPI.getPatientSummary(Number(patientId)),
      ]);
      setDetail(detailRes.data);
      setSummary(summaryRes.data);

      const diaryResponses = await Promise.all(
        DIARY_SECTIONS.map(async (section) => {
          try {
            const response = await section.loader(Number(patientId));
            return [section.key, (response.data || []) as any[]] as const;
          } catch (error: any) {
            const detailMessage = error.response?.data?.detail;
            if (detailMessage) {
              setDiaryError(detailMessage);
            }
            return [section.key, []] as const;
          }
        })
      );
      setDiaries(Object.fromEntries(diaryResponses));
    } catch (error: any) {
      console.error('Error loading patient workspace:', error);
      message.error(error.response?.data?.detail || 'Не удалось загрузить карточку пациентки');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    void loadPatientWorkspace();
  }, [loadPatientWorkspace]);

  const summaryCards = useMemo(() => {
    const latest = summary?.latest_metrics || {};
    return [
      latest.glucose?.glucose_level ? { title: 'Глюкоза', value: `${latest.glucose.glucose_level} ммоль/л` } : null,
      latest.weight?.weight_kg ? { title: 'Вес', value: `${latest.weight.weight_kg} кг` } : null,
      latest.abdomen?.abdomen_cm ? { title: 'Окружность живота', value: `${latest.abdomen.abdomen_cm} см` } : null,
      latest.lab_test?.test_type ? { title: 'Последний анализ', value: latest.lab_test.test_type } : null,
      latest.symptom?.symptom_text ? { title: 'Последний симптом', value: latest.symptom.symptom_text } : null,
    ].filter(Boolean) as { title: string; value: string }[];
  }, [summary]);

  const buildColumns = (rows: any[]) => {
    const sample = rows[0] || {};
    return Object.keys(sample)
      .filter((key) => !['id', 'user_id'].includes(key))
      .slice(0, 6)
      .map((key) => ({
        title: humanizeKey(key),
        dataIndex: key,
        key,
        render: (value: any) => formatValue(key, value),
      }));
  };

  const openPatientChat = async () => {
    try {
      const ensureResponse = await chatAPI.ensureConversationWithPatient(Number(patientId));
      const conversationId = (ensureResponse.data as any)?.id;
      if (conversationId) {
        navigate(`/chat/${conversationId}`);
        return;
      }
      message.info('Чат с этой пациенткой ещё не создан');
      navigate('/chats');
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Не удалось открыть чат');
    }
  };

  const handleArchive = async () => {
    if (!detail?.link?.id) return;
    try {
      setArchiveLoading(true);
      const values = await archiveForm.validateFields();
      await doctorLinkAPI.doctorArchiveLink(detail.link.id, values.comment ? { comment: values.comment } : undefined);
      message.success('Наблюдение архивировано');
      setArchiveOpen(false);
      archiveForm.resetFields();
      navigate('/dashboard');
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.detail || 'Не удалось архивировать наблюдение');
    } finally {
      setArchiveLoading(false);
    }
  };

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Назад
        </Button>

        <Card loading={loading} style={{ borderRadius: 18 }}>
          {!detail?.user ? (
            <Empty description="Карточка пациентки недоступна" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div>
                  <Title level={2} style={{ marginBottom: 8 }}>
                    {detail.user.full_name || `Пациентка #${detail.user.id}`}
                  </Title>
                  <Space wrap>
                    {detail.user.week_of_pregnancy ? <Tag color="blue">{detail.user.week_of_pregnancy} неделя</Tag> : null}
                    {detail.link?.access_level ? <Tag>{ACCESS_LABELS[detail.link.access_level] || detail.link.access_level}</Tag> : null}
                    {detail.link?.is_data_hidden_temporarily ? <Tag color="orange">Данные временно скрыты пациенткой</Tag> : null}
                    {detail.user.due_date ? <Tag color="purple">ПДР {dayjs(detail.user.due_date).format('DD.MM.YYYY')}</Tag> : null}
                  </Space>
                </div>
                <Space wrap>
                  <Button icon={<MessageOutlined />} onClick={openPatientChat}>
                    Открыть чаты
                  </Button>
                  {detail.link?.id ? (
                    <Button danger onClick={() => setArchiveOpen(true)}>
                      Архивировать наблюдение
                    </Button>
                  ) : null}
                </Space>
              </div>

              <Descriptions column={{ xs: 1, md: 2, xl: 4 }} bordered>
                <Descriptions.Item label="Email">{detail.user.email || '—'}</Descriptions.Item>
                <Descriptions.Item label="Телефон">{detail.user.phone || '—'}</Descriptions.Item>
                <Descriptions.Item label="Link ID">{detail.link?.id || 'legacy assignment'}</Descriptions.Item>
                <Descriptions.Item label="Последняя активность">
                  {summary?.last_activity_at ? dayjs(summary.last_activity_at).format('DD.MM.YYYY HH:mm') : 'Нет данных'}
                </Descriptions.Item>
              </Descriptions>

              {detail.link?.doctor_comment ? (
                <Alert type="info" showIcon message="Комментарий врача" description={detail.link.doctor_comment} />
              ) : null}

              {diaryError ? (
                <Alert type="warning" showIcon message={diaryError} />
              ) : null}
            </Space>
          )}
        </Card>

        <Card title="Сводка и последние показатели" style={{ borderRadius: 18 }}>
          {summaryCards.length === 0 ? (
            <Empty description="Пока нет последних метрик" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Row gutter={[12, 12]}>
              {summaryCards.map((card) => (
                <Col xs={24} md={12} xl={8} key={card.title}>
                  <Card size="small" style={{ borderRadius: 14 }}>
                    <Text type="secondary">{card.title}</Text>
                    <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{card.value}</div>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Card>

        <Card style={{ borderRadius: 18 }}>
          <Tabs
            items={DIARY_SECTIONS.map((section) => ({
              key: section.key,
              label: section.label,
              children: (diaries[section.key] || []).length ? (
                <Table
                  columns={buildColumns(diaries[section.key] || [])}
                  dataSource={diaries[section.key] || []}
                  rowKey={(row) => row.id}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: true }}
                />
              ) : (
                <Empty description={`Нет записей: ${section.label.toLowerCase()}`} image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ),
            }))}
          />
        </Card>
      </Space>

      <Modal
        title="Архивировать наблюдение"
        open={archiveOpen}
        onCancel={() => {
          setArchiveOpen(false);
          archiveForm.resetFields();
        }}
        onOk={handleArchive}
        okText="Архивировать"
        cancelText="Отмена"
        confirmLoading={archiveLoading}
      >
        <Form form={archiveForm} layout="vertical">
          <Form.Item name="comment" label="Комментарий врача (необязательно)">
            <TextArea rows={4} placeholder="Например: наблюдение завершено, рекомендован дальнейший очный контроль" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
